import { http, type PaginationMeta } from '@/shared/api'
import type {
  DeclineGiftResult,
  Gift,
  GiftOpening,
  GiftRefund,
  MyGiftCounts,
  MyGiftsFilter,
  MyGiftsPage,
} from '../model/types'

type MyGiftsMeta = PaginationMeta & { countsByStatus?: MyGiftCounts }

const giftPath = (giftId: string): string => `/app/gifts/${encodeURIComponent(giftId)}`

const sendPath = (token: string): string => `/app/gift-sends/${encodeURIComponent(token)}`

/**
 * Подарок получателя и дарителя (BE-51, BE-53). Два входа:
 * - **по токену отправки** (`startapp=s_<token>`) — карточка в чате и кнопки бота
 *   получателю; только так посторонний может увидеть закрытую коробку и открыть её;
 * - **по id подарка** (`startapp=g_<id>`, «Мои подарки», экраны решения и ваучера) —
 *   только свой подарок: постороннему всегда `NOT_FOUND`.
 *
 * Чтение по обоим входам отвечает 200 во всех исходах — что показать, решает `view`.
 */
export const giftApi = {
  /**
   * Счётчики «Моих подарков» (BE-55) — для статистики профиля. Счётчики
   * приходят в `meta` любого ответа списка и от фильтра не зависят, поэтому
   * хватает страницы из одного подарка. Нет их в ответе (старый бэкенд) — `null`.
   */
  counts: async (signal?: AbortSignal): Promise<MyGiftCounts | null> => {
    const { meta } = await http.get<Gift[]>('/app/gifts', { query: { limit: 1 }, signal })

    return (meta as MyGiftsMeta | undefined)?.countsByStatus ?? null
  },

  /**
   * «Мои подарки» и «История» (BE-54, BE-55): подаренные (`GIVER`, новые по
   * созданию сверху) или полученные (`RECEIVER`, новые по открытию коробки).
   * Кода и QR в элементах нет ни у одной роли. Статус чужой роли бэкенд молча
   * отбрасывает; неизвестный — `400`.
   */
  list: async (
    { role, statuses }: MyGiftsFilter,
    page: { limit: number; skip: number },
    signal?: AbortSignal,
  ): Promise<MyGiftsPage> => {
    const { data, meta } = await http.get<Gift[]>('/app/gifts', {
      query: { role, status: statuses?.join(','), limit: page.limit, skip: page.skip },
      signal,
    })
    const listMeta = meta as MyGiftsMeta | undefined

    return { items: data, total: listMeta?.total ?? data.length, counts: listMeta?.countsByStatus ?? null }
  },

  /**
   * «Отозвать» (БП-6): `INLINE_SENT → PAID`, карточка в чате перестаёт
   * открываться, токен отправки гаснет. Только даритель и только пока подарок
   * не открыли; иначе (открыли, уже отозван, чужой) — одинаково `404 GF-003`.
   * Ответ — карточка подарка дарителя (кода нет).
   */
  revoke: async (giftId: string): Promise<Gift> => (await http.post<Gift>(`${giftPath(giftId)}/revoke`)).data,

  /**
   * «Вернуть деньги» (БП-10): только `PAID` — подарок никому не вручён и в
   * реестр не попадал. Синхронно: провайдер согласился — `COMPLETED`, подарок
   * `REFUNDED`. Не `PAID` и чужой — `404 GF-003`; уже возвращён — `409 RF-003`;
   * провайдер отказал — `502 RF-004` (деньги и подарок не тронуты).
   */
  refund: async (giftId: string): Promise<GiftRefund> =>
    (await http.post<GiftRefund>(`${giftPath(giftId)}/refund`)).data,

  /** Свой подарок по id. Негодный id — `400`. */
  get: async (giftId: string, signal?: AbortSignal): Promise<GiftOpening> =>
    (await http.get<GiftOpening>(giftPath(giftId), { signal })).data,

  /**
   * Подарок по токену отправки — без изменений. Погасший, неизвестный и чужой
   * токен — `NOT_FOUND` без подробностей; токен не того вида — `400`.
   */
  getBySend: async (token: string, signal?: AbortSignal): Promise<GiftOpening> =>
    (await http.get<GiftOpening>(sendPath(token), { signal })).data,

  /**
   * Открыть коробку — `INLINE_SENT → OPENED`, атомарно на бэкенде (БП-7): в
   * групповом чате получателем становится первый, остальным — `NOT_FOUND`
   * (200, не ошибка). Успех — `AWAITING_DECISION` без кода и QR. Повтор тем
   * же человеком — тот же ответ или его текущий вид, если он уже решил.
   * Даритель — `OWNER`; заблокированный — `403 U-003`.
   */
  open: async (token: string): Promise<GiftOpening> => (await http.post<GiftOpening>(`${sendPath(token)}/open`)).data,

  /**
   * «Принять» — `OPENED → CLAIMED`: карточка с кодом, QR и точками. Повтор
   * (двойной тап, обрыв связи) — тот же ответ. Чужой — `404 GF-003`; решение
   * этого человека уже не принять (отказался, таймаут, возврат денег) — `409 GF-004`.
   */
  accept: async (giftId: string): Promise<Gift> => (await http.post<Gift>(`${giftPath(giftId)}/accept`)).data,

  /**
   * «Отказаться» — `OPENED → PAID`, подарок снова у дарителя. Повтор тем же
   * человеком — тот же ответ. Чужой — `404 GF-003`; подарок уже принят или
   * вернулся по таймауту — `409 GF-004`.
   */
  decline: async (giftId: string): Promise<DeclineGiftResult> =>
    (await http.post<DeclineGiftResult>(`${giftPath(giftId)}/decline`)).data,

  /**
   * Получатель увидел «погашение отменено» (BE-48): снимает отметку
   * `redemptionCancelledAt`. Только получатель, иначе `404 GF-003`; повтор — 200.
   * Ответ — как у `get`, уже без отметки: его кладут в кэш экрана.
   */
  acknowledgeRedemptionCancellation: async (giftId: string): Promise<GiftOpening> =>
    (await http.post<GiftOpening>(`${giftPath(giftId)}/redemption-cancellation/seen`)).data,
}
